import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import { useFormContext } from "../contexts/FormContext";
import MarkdownHint from "./MarkdownHint";

// ─────────────────────────────────────────────────────────────────────────────
// Styles partagés (définis en dehors pour éviter la recréation à chaque render)
// ─────────────────────────────────────────────────────────────────────────────

const tagStyle: React.CSSProperties = {
  marginBottom: "0.3rem",
  fontSize: "1rem",
  background: "#f5f6fa",
  borderRadius: "6px",
  padding: "0.5rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  cursor: "grab",
};

const addBtnStyle: React.CSSProperties = {
  padding: "0.3rem 0.7rem",
  borderRadius: "8px",
  border: "1px solid #ccc",
  background: "#eee",
  cursor: "pointer",
  fontSize: "1rem",
};

const removeBtnStyle: React.CSSProperties = {
  border: "none",
  background: "none",
  color: "red",
  cursor: "pointer",
  fontSize: "1rem",
};

// ─────────────────────────────────────────────────────────────────────────────
// FormationSubList — HORS du composant parent pour éviter le remount au keystroke
// ─────────────────────────────────────────────────────────────────────────────

interface FormationSubListProps {
  title: string;
  emoji: string;
  droppableId: string;
  items: string[];
  inputValue: string;
  setInputValue: (v: string) => void;
  onAdd: (v: string) => void;
  onRemove: (i: number) => void;
  onReorder: (result: any) => void;
  placeholder: string;
}

const FormationSubList: React.FC<FormationSubListProps> = ({
  title,
  emoji,
  droppableId,
  items,
  inputValue,
  setInputValue,
  onAdd,
  onRemove,
  onReorder,
  placeholder,
}) => (
  <div
    style={{
      border: "1px solid #e3e6ee",
      borderRadius: "10px",
      padding: "1rem",
      marginBottom: "0.8rem",
      background: "#f9fafc",
    }}
  >
    <div style={{ fontWeight: 600, color: "#1E3C73", marginBottom: "0.6rem" }}>
      {emoji} {title}
    </div>
    <div style={{ display: "flex", gap: "0.6rem", marginBottom: "0.5rem" }}>
      <input
        type="text"
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (inputValue.trim()) {
              onAdd(inputValue.trim());
              setInputValue("");
            }
          }
        }}
        style={{
          flex: 1,
          padding: "0.6rem",
          borderRadius: "8px",
          border: "1px solid #ccc",
          fontSize: "1rem",
        }}
      />
      <button
        type="button"
        onClick={() => {
          if (inputValue.trim()) {
            onAdd(inputValue.trim());
            setInputValue("");
          }
        }}
        style={addBtnStyle}
      >
        Ajouter
      </button>
    </div>
    <DragDropContext onDragEnd={onReorder}>
      <Droppable droppableId={droppableId}>
        {(provided) => (
          <ul
            {...provided.droppableProps}
            ref={provided.innerRef}
            style={{ padding: 0, listStyle: "none", margin: 0 }}
          >
            {items.map((item, idx) => (
              <Draggable key={item + idx} draggableId={droppableId + idx} index={idx}>
                {(provided) => (
                  <li
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={{ ...provided.draggableProps.style, ...tagStyle }}
                  >
                    <span>📌 {item}</span>
                    <button type="button" onClick={() => onRemove(idx)} style={removeBtnStyle}>
                      ✕
                    </button>
                  </li>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </ul>
        )}
      </Droppable>
    </DragDropContext>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// PersonalInfoForm
// ─────────────────────────────────────────────────────────────────────────────

const PersonalInfoForm: React.FC = () => {
  const { formData, updateFormData } = useFormContext();

  const [fullName, setFullName] = useState(formData.full_name);
  const [email, setEmail] = useState(formData.email);
  const [phone, setPhone] = useState(formData.phone ?? "");
  const [linkedin, setLinkedin] = useState(formData.linkedin ?? "");
  const [photo, setPhoto] = useState<string | null>(formData.photo_base64);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [jobTitles, setJobTitles] = useState<string[]>(formData.job_titles);
  const [jobTitleInput, setJobTitleInput] = useState("");
  const [aPropos, setAPropos] = useState(formData.a_propos);
  const [languages, setLanguages] = useState<{ name: string; level: string }[]>(formData.languages);
  const [languageInput, setLanguageInput] = useState("");
  const [languageLevelInput, setLanguageLevelInput] = useState("");
  const [localisation, setLocalisation] = useState(formData.localisation);

  const [recentFormations, setRecentFormations] = useState<string[]>(
    (formData as any).recent_education ?? []
  );
  const [recentInput, setRecentInput] = useState("");
  const [diplomaFormations, setDiplomaFormations] = useState<string[]>(
    (formData as any).diploma_education ?? []
  );
  const [diplomaInput, setDiplomaInput] = useState("");

  const [jobTitleOptions, setJobTitleOptions] = useState<string[]>([]);
  const [jobTitleLoading, setJobTitleLoading] = useState(false);
  const [jobTitleOpen, setJobTitleOpen] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (!jobTitleInput.trim()) {
      setJobTitleOptions([]);
      setJobTitleOpen(false);
      return;
    }
    setJobTitleLoading(true);
    fetch(`${API_URL}/skills/job-titles?q=${encodeURIComponent(jobTitleInput)}`)
      .then((r) => r.json())
      .then((results: string[]) => {
        setJobTitleOptions(results);
        setJobTitleOpen(results.length > 0);
        setJobTitleLoading(false);
      })
      .catch(() => {
        setJobTitleOptions([]);
        setJobTitleOpen(false);
        setJobTitleLoading(false);
      });
  }, [jobTitleInput]);

  // ── Sync to FormContext ──────────────────────────────────────────────────
  useEffect(() => { updateFormData("full_name", fullName); }, [fullName]);
  useEffect(() => { updateFormData("email", email); }, [email]);
  useEffect(() => { (updateFormData as any)("phone", phone); }, [phone]);
  useEffect(() => { (updateFormData as any)("linkedin", linkedin); }, [linkedin]);
  useEffect(() => { updateFormData("photo_base64", photo); }, [photo]);
  useEffect(() => { updateFormData("job_titles", jobTitles); }, [jobTitles]);
  useEffect(() => { updateFormData("a_propos", aPropos); }, [aPropos]);
  useEffect(() => { updateFormData("languages", languages); }, [languages]);
  useEffect(() => { updateFormData("localisation", localisation); }, [localisation]);

  useEffect(() => {
    (updateFormData as any)("recent_education", recentFormations);
    (updateFormData as any)("diploma_education", diplomaFormations);
    updateFormData("formation_initiales", [...recentFormations, ...diplomaFormations]);
  }, [recentFormations, diplomaFormations]);

  // ── Drag & drop ──────────────────────────────────────────────────────────
  const handleJobTitlesReorder = (result: any) => {
    if (!result.destination) return;
    const items = [...jobTitles];
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setJobTitles(items);
  };

  const handleRecentReorder = (result: any) => {
    if (!result.destination) return;
    const items = [...recentFormations];
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setRecentFormations(items);
  };

  const handleDiplomaReorder = (result: any) => {
    if (!result.destination) return;
    const items = [...diplomaFormations];
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setDiplomaFormations(items);
  };

  const handleLanguagesReorder = (result: any) => {
    if (!result.destination) return;
    const items = [...languages];
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setLanguages(items);
  };

  // ── Photo ────────────────────────────────────────────────────────────────
  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Veuillez sélectionner un fichier image (JPG, PNG, etc.)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert("La taille de l'image ne doit pas dépasser 5MB");
      return;
    }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPhoto(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handlePhotoRemove = () => {
    setPhoto(null);
    setPhotoFile(null);
  };

  // ── Styles ───────────────────────────────────────────────────────────────
  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "0.7rem",
    borderRadius: "8px",
    border: "1px solid #ccc",
    fontSize: "1rem",
    boxSizing: "border-box",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    marginBottom: "0.5rem",
    fontWeight: 500,
  };

  const subLabelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "0.9em",
    color: "#888",
    marginTop: "0.3rem",
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* ── Identité ─────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: "1.2rem" }}>
        <label style={labelStyle}>Nom complet :</label>
        <input
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          required
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: "1.2rem" }}>
        <label style={labelStyle}>Email :</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          style={inputStyle}
          placeholder="votre.email@example.com"
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1.2rem" }}>
        <div>
          <label style={labelStyle}>
            Téléphone :
            <span style={subLabelStyle}>Affiché sur le CV</span>
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            style={inputStyle}
            placeholder="+33 6 00 00 00 00"
          />
        </div>
        <div>
          <label style={labelStyle}>
            URL LinkedIn :
            <span style={subLabelStyle}>Lien cliquable dans le CV</span>
          </label>
          <input
            type="url"
            value={linkedin}
            onChange={(e) => setLinkedin(e.target.value)}
            style={inputStyle}
            placeholder="https://linkedin.com/in/votre-profil"
          />
        </div>
      </div>

      <div style={{ marginBottom: "1.2rem" }}>
        <label style={labelStyle}>Localisation :</label>
        <input
          type="text"
          value={localisation}
          onChange={(e) => setLocalisation(e.target.value)}
          required
          style={inputStyle}
          placeholder="Paris, France"
        />
      </div>

      {/* ── Photo ────────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: "1.2rem" }}>
        <label style={labelStyle}>
          Photo de profil (optionnel) :
          <span style={subLabelStyle}>Formats acceptés : JPG, PNG, GIF (max 5MB)</span>
        </label>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          {photo ? (
            <div style={{ position: "relative" }}>
              <img
                src={photo}
                alt="Aperçu"
                style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "8px", border: "2px solid #1E3C73" }}
              />
              <button
                type="button"
                onClick={handlePhotoRemove}
                style={{ position: "absolute", top: "-8px", right: "-8px", background: "red", color: "white", border: "none", borderRadius: "50%", width: "24px", height: "24px", cursor: "pointer", fontSize: "12px" }}
              >
                ✕
              </button>
            </div>
          ) : (
            <div style={{ width: "100px", height: "100px", border: "2px dashed #ccc", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2rem" }}>
              📷
            </div>
          )}
          <div style={{ flex: 1 }}>
            <input type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: "none" }} id="photo-upload" />
            <label
              htmlFor="photo-upload"
              style={{ display: "inline-block", padding: "0.3rem 0.7rem", background: "#eee", color: "#333", border: "1px solid #ccc", borderRadius: "8px", cursor: "pointer", fontSize: "1rem" }}
            >
              {photo ? "Changer la photo" : "Ajouter une photo"}
            </label>
            {photoFile && (
              <p style={{ margin: "0.5rem 0 0 0", fontSize: "0.9em", color: "#666" }}>
                {photoFile.name} ({Math.round(photoFile.size / 1024)} KB)
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── Intitulés du poste ────────────────────────────────────────────── */}
      <div style={{ marginBottom: "1.2rem" }}>
        <label style={labelStyle}>
          Intitulés du poste :
          <span style={subLabelStyle}>Exemple : "Consultant Senior, Chef de projet"</span>
        </label>
        <div style={{ display: "flex", gap: "1rem", marginBottom: "0.5rem" }}>
          <Autocomplete
            freeSolo
            options={jobTitleOptions}
            loading={jobTitleLoading}
            open={jobTitleOpen}
            onOpen={() => jobTitleOptions.length > 0 && setJobTitleOpen(true)}
            onClose={() => setJobTitleOpen(false)}
            inputValue={jobTitleInput}
            onInputChange={(_, v) => setJobTitleInput(v)}
            onChange={(_, v) => {
              if (v && typeof v === "string") {
                setJobTitles([...jobTitles, v.trim()]);
                setJobTitleInput("");
                setJobTitleOpen(false);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Ajouter un intitulé"
                variant="outlined"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && jobTitleInput.trim()) {
                    e.preventDefault();
                    e.stopPropagation();
                    setJobTitles([...jobTitles, jobTitleInput.trim()]);
                    setJobTitleInput("");
                    setJobTitleOpen(false);
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {jobTitleLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ flex: 2 }}
          />
          <button
            type="button"
            onClick={() => {
              if (jobTitleInput.trim()) {
                setJobTitles([...jobTitles, jobTitleInput.trim()]);
                setJobTitleInput("");
                setJobTitleOpen(false);
              }
            }}
            style={addBtnStyle}
          >
            Ajouter
          </button>
        </div>
        <DragDropContext onDragEnd={handleJobTitlesReorder}>
          <Droppable droppableId="jobTitles">
            {(provided) => (
              <ul {...provided.droppableProps} ref={provided.innerRef} style={{ padding: 0, listStyle: "none" }}>
                {jobTitles.map((title, idx) => (
                  <Draggable key={title + idx} draggableId={"jt-" + idx} index={idx}>
                    {(provided) => (
                      <li
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{ ...provided.draggableProps.style, ...tagStyle }}
                      >
                        <span>💼 {title}</span>
                        <button
                          type="button"
                          onClick={() => setJobTitles(jobTitles.filter((_, i) => i !== idx))}
                          style={removeBtnStyle}
                        >
                          ✕
                        </button>
                      </li>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {/* ── Formations et spécialisations ─────────────────────────────────── */}
      <div style={{ marginBottom: "1.2rem" }}>
        <label style={labelStyle}>
          Formations et spécialisations :
          <span style={subLabelStyle}>
            Séparez les formations récentes des diplômes académiques pour un meilleur rendu dans le CV.
          </span>
        </label>

        {/* ✅ FormationSubList est maintenant un composant stable défini en dehors */}
        <FormationSubList
          title="Formations récentes et spécialisations"
          emoji="🎓"
          droppableId="recentFormations"
          items={recentFormations}
          inputValue={recentInput}
          setInputValue={setRecentInput}
          onAdd={(v) => setRecentFormations([...recentFormations, v])}
          onRemove={(i) => setRecentFormations(recentFormations.filter((_, idx) => idx !== i))}
          onReorder={handleRecentReorder}
          placeholder="Ex : Managers du changement – ESSEC Business School (2024)"
        />

        <FormationSubList
          title="Diplômes académiques"
          emoji="🏛️"
          droppableId="diplomaFormations"
          items={diplomaFormations}
          inputValue={diplomaInput}
          setInputValue={setDiplomaInput}
          onAdd={(v) => setDiplomaFormations([...diplomaFormations, v])}
          onRemove={(i) => setDiplomaFormations(diplomaFormations.filter((_, idx) => idx !== i))}
          onReorder={handleDiplomaReorder}
          placeholder="Ex : Master 2 Droit public – Université Lyon 3 (2013)"
        />
      </div>

      {/* ── À propos ──────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: "1.2rem" }}>
        <label style={labelStyle}>À propos de moi :</label>
        <MarkdownHint />
        <textarea
          value={aPropos}
          onChange={(e) => setAPropos(e.target.value)}
          placeholder={`Exemple :\n\nForte d'une solide expérience en **gestion de produit** et transformation digitale...\n\n**Expertises clés :**\n- Cadrage stratégique\n- Pilotage agile (*Scrum, Kanban*)`}
          rows={10}
          style={{
            width: "100%",
            maxWidth: "100%",
            padding: "0.7rem",
            borderRadius: "8px",
            border: "1px solid #ccc",
            fontSize: "1rem",
            fontFamily: "inherit",
            resize: "vertical",
            boxSizing: "border-box",
            lineHeight: "1.5",
          }}
          required
        />
      </div>

      {/* ── Langues ───────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: "1.2rem" }}>
        <label style={labelStyle}>
          Langues et niveaux :
          <span style={subLabelStyle}>Ajoutez les langues et leur niveau</span>
        </label>
        <div style={{ display: "flex", gap: "1rem", marginBottom: "0.5rem" }}>
          <input
            type="text"
            placeholder="Langue"
            value={languageInput}
            onChange={(e) => setLanguageInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (languageInput.trim() && languageLevelInput.trim()) {
                  setLanguages([...languages, { name: languageInput.trim(), level: languageLevelInput }]);
                  setLanguageInput("");
                  setLanguageLevelInput("");
                }
              }
            }}
            style={{ flex: 2, padding: "0.7rem", borderRadius: "8px", border: "1px solid #ccc", fontSize: "1rem" }}
          />
          <select
            value={languageLevelInput}
            onChange={(e) => setLanguageLevelInput(e.target.value)}
            style={{ flex: 1, padding: "0.7rem", borderRadius: "8px", border: "1px solid #ccc", fontSize: "1rem" }}
          >
            <option value="">Niveau</option>
            <option value="A1">A1</option>
            <option value="A2">A2</option>
            <option value="B1">B1</option>
            <option value="B2">B2</option>
            <option value="C1">C1</option>
            <option value="C2">C2</option>
            <option value="Native">Natif</option>
          </select>
          <button
            type="button"
            onClick={() => {
              if (languageInput.trim() && languageLevelInput.trim()) {
                setLanguages([...languages, { name: languageInput.trim(), level: languageLevelInput }]);
                setLanguageInput("");
                setLanguageLevelInput("");
              }
            }}
            style={addBtnStyle}
          >
            Ajouter
          </button>
        </div>
        <DragDropContext onDragEnd={handleLanguagesReorder}>
          <Droppable droppableId="languages">
            {(provided) => (
              <ul {...provided.droppableProps} ref={provided.innerRef} style={{ padding: 0, listStyle: "none" }}>
                {languages.map((lang, idx) => (
                  <Draggable key={`lang-${idx}`} draggableId={`lang-${idx}`} index={idx}>
                    {(provided) => (
                      <li
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{ ...provided.draggableProps.style, ...tagStyle }}
                      >
                        <span>🌐 {lang.name} — {lang.level}</span>
                        <button
                          type="button"
                          onClick={() => setLanguages(languages.filter((_, i) => i !== idx))}
                          style={removeBtnStyle}
                        >
                          ✕
                        </button>
                      </li>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </div>
  );
};

export default PersonalInfoForm;